import { computed, ref, watch } from 'vue';
import {
  compactQuery,
  formatNumber,
  numericValue,
  queryValue,
} from '../utils/qprecoFormatters';
import {
  PAGE_SIZE,
  purposeOptions,
  sourceOptions,
} from '../utils/qprecoConstants';

/**
 * Estado reativo dos filtros, da paginação e da navegação por query string da
 * listagem da Área QPreço.
 *
 * Extraído de `pages/AreaQprecoIndex.vue` sem alterar nomes de query param,
 * defaults nem os rótulos dos chips ativos.
 */
export function useQprecoListQuery({ router, route, accountId, listMeta }) {
  const searchInput = ref('');
  const cityInput = ref('');
  const neighborhoodInput = ref('');
  const sourceInput = ref('external');
  const purposeInput = ref('');

  const queryPage = computed(() => {
    const page = Number.parseInt(queryValue(route.query.page), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  });

  const queryFilters = computed(() => ({
    q: queryValue(route.query.q),
    cidade: queryValue(route.query.cidade),
    bairro: queryValue(route.query.bairro),
    finalidade: queryValue(route.query.finalidade),
    source: queryValue(route.query.source) || 'external',
  }));

  const totalResults = computed(() => numericValue(listMeta.value?.count));
  const totalPages = computed(() =>
    Math.max(
      1,
      numericValue(listMeta.value?.total_pages) ||
        Math.ceil(totalResults.value / PAGE_SIZE)
    )
  );
  const pageStart = computed(() =>
    totalResults.value ? (queryPage.value - 1) * PAGE_SIZE + 1 : 0
  );
  const pageEnd = computed(() =>
    Math.min(queryPage.value * PAGE_SIZE, totalResults.value)
  );
  const visiblePages = computed(() => {
    const total = totalPages.value;
    const current = queryPage.value;
    const start = Math.max(1, Math.min(current - 2, total - 4));
    const end = Math.min(total, start + 4);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  });

  const sourceLabel = computed(
    () =>
      sourceOptions.find(option => option.value === queryFilters.value.source)
        ?.label || 'Base integrada Imudei'
  );
  const sourceHelper = computed(() =>
    queryFilters.value.source === 'local'
      ? 'Imóveis já cadastrados na Imudei, com histórico e ações internas.'
      : 'Base integrada processada a partir dos dados da Imudei.'
  );

  const activeFilterChips = computed(() => {
    const filters = queryFilters.value;
    const chips = [];
    if (filters.source && filters.source !== 'external') {
      chips.push({ key: 'source', label: `Origem: ${sourceLabel.value}` });
    }
    if (filters.q) chips.push({ key: 'q', label: `Busca: ${filters.q}` });
    if (filters.cidade) {
      chips.push({ key: 'cidade', label: `Cidade: ${filters.cidade}` });
    }
    if (filters.bairro) {
      chips.push({ key: 'bairro', label: `Bairro: ${filters.bairro}` });
    }
    if (filters.finalidade) {
      const purposeLabel =
        purposeOptions.find(option => option.value === filters.finalidade)
          ?.label || filters.finalidade;
      chips.push({ key: 'finalidade', label: `Finalidade: ${purposeLabel}` });
    }
    return chips;
  });

  const activeFiltersCount = computed(() => activeFilterChips.value.length);

  const resultRangeLabel = computed(() =>
    totalResults.value
      ? `${formatNumber(pageStart.value)}-${formatNumber(pageEnd.value)}`
      : '0'
  );

  const syncFilterInputsFromRoute = () => {
    const filters = queryFilters.value;
    searchInput.value = filters.q;
    cityInput.value = filters.cidade;
    neighborhoodInput.value = filters.bairro;
    sourceInput.value = filters.source;
    purposeInput.value = filters.finalidade;
  };

  const updateListQuery = query => {
    router.push({
      name: 'lais_area_qpreco_index',
      params: { accountId: accountId.value },
      query: compactQuery(query),
    });
  };

  const applyFilters = () => {
    updateListQuery({
      q: searchInput.value.trim(),
      cidade: cityInput.value.trim(),
      bairro: neighborhoodInput.value.trim(),
      finalidade: purposeInput.value,
      source: sourceInput.value || 'external',
      page: 1,
    });
  };

  const clearFilters = () => {
    searchInput.value = '';
    cityInput.value = '';
    neighborhoodInput.value = '';
    purposeInput.value = '';
    sourceInput.value = 'external';
    updateListQuery({ source: 'external', page: 1 });
  };

  const removeFilter = key => {
    const nextFilters = { ...queryFilters.value, [key]: '', page: 1 };
    if (key === 'q') searchInput.value = '';
    if (key === 'cidade') cityInput.value = '';
    if (key === 'bairro') neighborhoodInput.value = '';
    if (key === 'finalidade') purposeInput.value = '';
    if (key === 'source') sourceInput.value = 'external';
    updateListQuery(nextFilters);
  };

  const selectSource = source => {
    sourceInput.value = source;
    applyFilters();
  };

  const goToPage = page => {
    const nextPage = Math.min(Math.max(Number(page) || 1, 1), totalPages.value);
    updateListQuery({
      ...queryFilters.value,
      page: nextPage,
    });
  };

  watch(
    () => route.query,
    () => {
      syncFilterInputsFromRoute();
    },
    { immediate: true }
  );

  return {
    searchInput,
    cityInput,
    neighborhoodInput,
    sourceInput,
    purposeInput,
    queryPage,
    queryFilters,
    totalResults,
    totalPages,
    pageStart,
    pageEnd,
    visiblePages,
    sourceLabel,
    sourceHelper,
    activeFilterChips,
    activeFiltersCount,
    resultRangeLabel,
    updateListQuery,
    applyFilters,
    clearFilters,
    removeFilter,
    selectSource,
    goToPage,
  };
}
